import type { ReportEvent } from '@/types/report';
import { getReportEventTime } from '@/utils/report-event-time';
import type { ReportEventSortOption } from '../constants';

function compareText(left: string, right: string): number {
    if (left === right) {
        return 0;
    }
    return left < right ? -1 : 1;
}

// Events with no value sort first, and no arithmetic runs on the missing case,
// so the comparator can never return NaN and skip the tie-breakers below.
function compareOptionalNumbers(
    left: number | null | undefined,
    right: number | null | undefined,
): number {
    if (left == null && right == null) {
        return 0;
    }
    if (left == null) {
        return -1;
    }
    if (right == null) {
        return 1;
    }
    return left - right;
}

function sortValue(
    event: ReportEvent,
    field: Exclude<ReportEventSortOption['field'], 'manual'>,
): number | null | undefined {
    if (field === 'confidence') {
        return event.confidence;
    }
    if (field === 'updatedAt') {
        return event.updatedAt;
    }
    return getReportEventTime(event);
}

// The single source of truth for every derived report event order: the list on
// screen, the documents built in the browser, and the order sent to the backend
// for ZIP exports and for persisting a sort as the manual order.
export function sortReportEvents(
    events: ReportEvent[],
    sortOption: ReportEventSortOption,
): ReportEvent[] {
    if (sortOption.field === 'manual') {
        return [...events];
    }
    const { field, direction } = sortOption;
    const sign = direction === 'asc' ? 1 : -1;
    return [...events].sort(
        (left, right) =>
            sign *
            (compareOptionalNumbers(
                sortValue(left, field),
                sortValue(right, field),
            ) ||
                compareText(left.eventType, right.eventType) ||
                compareText(left.id, right.id)),
    );
}
